import React, { useState, useEffect, useRef } from 'react';
import IconButton from '@mui/material/IconButton';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { styled } from '@mui/material/styles';

import MenuIcon from '@mui/icons-material/Menu';
import ItarraIcon from '../icons/ItarraIcon';
import LightsIcon from '../icons/LightsIcon';
import HydralicSpoolValveIcon from '../icons/HydralicSpoolValveIcon';
import SinglePhaseIcon from '../icons/SinglePhaseIcon';
import ThreePhaseIcon from '../icons/ThreePhaseIcon';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import RearLinkageIcon from '../icons/RearLinkage';
import FrontLinkageIcon from '../icons/FrontLinkage';
import LightsComponent from './LightsComponent';
import HydraulicsComponent from './HydraulicsComponent';
import TiltIcon from '../icons/TiltIcon';

const MenuContainer = styled(Paper)(({ theme, width }) => ({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  background: theme.palette.background.paper,
  zIndex: 1300,
  width: width,
}));

const StyledTypography = styled(Typography)({
  textAlign: 'center',
  width: '100%',
  marginTop: '-5px',
});

const CenterButton = styled(IconButton)(({ theme }) => ({
  position: 'fixed',
  top: theme.spacing(2),
  left: theme.spacing(2),
  zIndex: 1400,
}));

const MenuItem = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  border: '1px solid white',
  margin: theme.spacing(1),
  width: '80px',
  height: '80px',
  cursor: 'pointer',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
}));

// Snackbar Alert wrapper
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const SquareMenu = ({ setMenu }) => {
  const [open, setOpen] = useState(false);
  const [containerWidth, setContainerWidth] = useState('auto');
  const [selectedItem, setSelectedItem] = useState(null);
  const containerRef = useRef(null);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Items disabled for the demo
  const disabledItems = ['Lights','Drive','Rear Linkage','Single Phase', 'Three Phase', 'Hydraulics'];

  const toggleMenu = () => {
    setOpen(!open);
  };

  const menuItems = [
    { icon: <ItarraIcon size={40} />, text: 'Drive', margin: '-5px' },
    { icon: <LightsIcon size={30} />, text: 'Lights', margin: '-5px' },
    { icon: <FrontLinkageIcon size={33} />, text: 'Front Linkage', margin: '-5px' },
    { icon: <RearLinkageIcon size={33} />, text: 'Rear Linkage', margin: '-5px' },
    { icon: <TiltIcon size={35} />, text: 'Tilt', margin: '-5px' },
    { icon: <SinglePhaseIcon size={50} />, text: 'Single Phase', margin: '-18px' },
    { icon: <ThreePhaseIcon size={50} />, text: 'Three Phase', margin: '-18px' },
    { icon: <HydralicSpoolValveIcon size={30} />, text: 'Hydraulics', margin: '-5px' },
    { icon: <ExitToAppIcon />, text: 'Exit', margin: '-5px' },
  ];

  const handleItemClick = (item) => {
    if (disabledItems.includes(item)) {
      setSnackbarOpen(true);
      return;
    }

    if (
      item !== 'Exit' &&
      item !== 'Drive' &&
      item !== 'Tilt' &&
      item !== 'Front Linkage' &&
      item !== 'Rear Linkage' &&
      item !== 'Front Hydraulics' &&
      item !== 'Rear Hydraulics 1' &&
      item !== 'Rear Hydraulics 2'
    ) {
      setSelectedItem(item);
    } else {
      setMenu(item);
      setSelectedItem(null);
      toggleMenu();
    }
  };

  const handleBackToMenu = () => {
    setSelectedItem(null);
    setOpen(true);
  };

  useEffect(() => {
    if (containerRef.current) {
      const itemWidth = 80;
      const padding = 20;
      const numItems = 3;
      const totalPadding = padding * (numItems + 1);
      const width = numItems * itemWidth + totalPadding + 40;
      setContainerWidth(`${width}px`);
    }
  }, [open]);

  return (
    <>
      <CenterButton color="primary" onClick={toggleMenu} size="large">
        <MenuIcon />
      </CenterButton>

      {open && (
        <MenuContainer elevation={4} ref={containerRef} width={containerWidth}>
          <Grid container spacing={2} justifyContent="center" alignItems="center">
            {menuItems.map((item, index) => (
              <Grid item xs={4} key={index}>
                <Tooltip title={item.text}>
                  <MenuItem
                    onClick={() => handleItemClick(item.text)}
                    style={{
                      borderColor: disabledItems.includes(item.text) ? 'red' : 'white',
                      opacity: disabledItems.includes(item.text) ? 0.6 : 1,
                    }}
                  >
                    <IconButton color="default">{item.icon}</IconButton>
                    <StyledTypography
                      variant="caption"
                      style={{
                        fontSize: 10,
                        marginTop: item.margin,
                        color: disabledItems.includes(item.text) ? 'red' : 'inherit',
                      }}
                    >
                      {item.text}
                    </StyledTypography>
                  </MenuItem>
                </Tooltip>
              </Grid>
            ))}
          </Grid>
        </MenuContainer>
      )}

      {selectedItem && (
        <MenuContainer elevation={4} width={containerWidth}>
          {selectedItem === 'Lights' && (
            <LightsComponent Icon={LightsIcon} label="Lights" onBackToMenu={handleBackToMenu} />
          )}
          {selectedItem === 'Single Phase' && (
            <LightsComponent Icon={SinglePhaseIcon} label="Single Phase" onBackToMenu={handleBackToMenu} />
          )}
          {selectedItem === 'Three Phase' && (
            <LightsComponent Icon={ThreePhaseIcon} label="Three Phase" onBackToMenu={handleBackToMenu} />
          )}
          {selectedItem === 'Hydraulics' && (
            <HydraulicsComponent onBackToMenu={handleBackToMenu} handleItemClick={handleItemClick} />
          )}
        </MenuContainer>
      )}

      {/* Snackbar for disabled items */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" onClose={() => setSnackbarOpen(false)}>
          Sorry, disabled for the demo. Ask the team for a full field demo.
        </Alert>
      </Snackbar>
    </>
  );
};

export default SquareMenu;
